import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FicheConseil, FicheConseilFilterInput, FichesPaginatedCollectionSegment } from '@lib/models/generated/graphql';
import { gql } from 'apollo-angular';
import { DEFAULT_PAGINATION_PARAMS, PaginationParams, QueryManagerService } from 'finzzle-angular-utils';

import { firstValueFrom } from 'rxjs';
import { FicheConseilTableItem } from 'src/app/components/my-fiches/fiche-conseil-dashboard/fiche-conseil-table/fiche-conseil-table.component';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';

export type FicheConseilSegment = {
  items: FicheConseilTableItem[];
  totalCount: number;
  pageInfo: { hasNextPage: boolean; hasPreviousPage: boolean };
};
export const ficheConseilListTableFragment = gql`
  fragment ficheConseilListTable on FicheConseil {
    dateModification
    id
    investisseur {
      id
      displayName
      nom
    }
    coInvestisseur {
      id
      displayName
      nom
    }
    natureOperation {
      id
      libelle
    }
    libelle
    dateModification
    enveloppe {
      code
      libelle
    }
    # produit {
    #   libelle
    # }
    versements {
      versementInitial {
        montantBrut
      }
      versementComplementaire {
        montantBrut
      }
    }
    statut
    dateCreation
    conseilConfigId
  }
`;
export const FICHECONSEIL_LIST_CONSULTANT_TABLE = gql`
  query fichesPaginated($skip: Int, $take: Int, $where: FicheConseilFilterInput, $order: [FicheConseilSortInput!]) {
    fichesPaginated(skip: $skip, take: $take, where: $where, order: $order) {
      items {
        ...ficheConseilListTable
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${ficheConseilListTableFragment}
`;
@Component({
  selector: 'app-fiche-conseil-dashboard',
  templateUrl: './fiche-conseil-dashboard.component.html',
  styleUrls: ['./fiche-conseil-dashboard.component.scss'],
})
export class FicheConseilDashboardComponent {
  requestResult: { loading: boolean; data: FicheConseilSegment } | undefined;
  requestFilter: FicheConseilFilterInput;
  paginationParams: PaginationParams = DEFAULT_PAGINATION_PARAMS;

  stateFilter: FicheConseilFilterInput | null = null;
  filterInvestisseurId: number | null | undefined;

  constructor(
    private queryManager: QueryManagerService,
    private datePipe: DatePipe,
    private activeRoute: ActivatedRoute,
    private ficheConseilService: FicheConseilService
  ) {}
  ngOnInit(): void {
    this.getFicheConseils();
    this.checkToDisplayFicheConseil();
  }

  checkToDisplayFicheConseil() {
    this.activeRoute.queryParams.subscribe(params => {
      if (params['ficheId']) {
        this.ficheConseilService.displayPDF(params['ficheId']);
      }
    });
  }

  async getFicheConseils() {
    const order = [
      {
        dateModification: 'DESC',
      },
    ];
    this.requestFilter = { and: [] };
    if (this.stateFilter) {
      this.requestFilter.and?.push(this.stateFilter);
    }
    if (this.filterInvestisseurId) {
      this.requestFilter.and?.push({
        or: [
          { investisseur: { id: { eq: this.filterInvestisseurId } } },
          { coInvestisseur: { id: { eq: this.filterInvestisseurId } } },
        ],
      });
    }
    const requestVariables = {
      ...this.paginationParams,
      where: this.requestFilter,
      order,
    };
    const result = await firstValueFrom(
      this.queryManager.query<{ fichesPaginated: FichesPaginatedCollectionSegment }>({
        query: FICHECONSEIL_LIST_CONSULTANT_TABLE,
        variables: requestVariables,
        fetchPolicy: 'network-only',
      })
    );
    if (result?.data?.fichesPaginated) {
      this.requestResult = {
        loading: result.loading,
        data: {
          totalCount: result.data.fichesPaginated.totalCount,
          items: this.ficheConseilToTableItem(result.data.fichesPaginated.items),
          pageInfo: result.data.fichesPaginated.pageInfo,
        },
      };
    }
  }

  onStateFilterChange(filter: FicheConseilFilterInput | null) {
    if (filter === this.stateFilter) return;
    this.stateFilter = filter;
    this.getFicheConseils();
  }

  onInvestisseurIdSelected(investisseurId: number | null | undefined) {
    if (investisseurId === this.filterInvestisseurId) return;
    this.filterInvestisseurId = investisseurId;
    this.getFicheConseils();
  }

  ficheConseilToTableItem(input: FicheConseil[] | undefined | null): FicheConseilTableItem[] {
    if (!input) return [] as FicheConseilTableItem[];
    return input.map((item: FicheConseil) => {
      return {
        id: item.id,
        conseilConfigId: item.conseilConfigId,
        natureOperationId: item.natureOperation?.id ?? '-',
        natureOperationLabel: item.natureOperation?.libelle ?? '-',

        label: item.libelle ?? this.defaultFicheLabel(item),
        investisseurName: this.getInvestisseurFullDisplayName(item),
        enveloppeName: item.enveloppe?.libelle ?? '-',
        montant: this.getMontant(item),
        statut: item.statut,
        produitName: item.produit?.libelle ?? '-',
        dateModification: item.dateModification,
      };
    });
  }

  getInvestisseurFullDisplayName(fiche: FicheConseil): string {
    if (!fiche.investisseur) {
      return '-';
    }
    let displayName = fiche.investisseur.displayName ?? '-';
    if (fiche.coInvestisseur) {
      displayName += `\n& ${fiche.coInvestisseur.displayName}`;
    }
    return displayName;
  }

  defaultFicheLabel(fiche: FicheConseil) {
    return this.ficheConseilService.getFicheConseilLabel(fiche, this.datePipe);
  }

  getMontant(fiche: FicheConseil) {
    if (fiche.natureOperation?.id === 2) {
      return fiche.versements?.versementComplementaire?.montantBrut ?? 0;
    }
    return fiche.versements?.versementInitial?.montantBrut ?? 0;
  }
}
